import React, { useState, useEffect } from 'react';
import {
  Form,
  FormInstance,
  FormErrors,
  TextInput,
  Checkbox
} from 'blueprint-form';
import Button from '../../molecules/button';
import Callout from '../../organisms/callout';
import Card from '../../organisms/card';
import ErrorCallout from '../../organisms/callout/error-callout';
import { LoadingBlock } from '../../organisms/loading/progress';
import Modal from '../../organisms/modal';
import PasswordInput from '../../organisms/password';
import Icon from '../../atoms/icon';
import Meta from '../../atoms/meta';
import { faPlusHexagon } from '@fortawesome/pro-solid-svg-icons';
import { useGetInvitedUser } from '../../../libs/user';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { BaseResponse, PostJSON } from '../../../libs/request';

interface NewUserData {
  email?: string;
  password: string;
  confirm: string;
  eula: string;
}

const NewUserPage = (): JSX.Element => {
  const { search } = useLocation();
  const query = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  const token = query && query.token ? (query.token as string) : null;
  const { user, isLoading, error } = useGetInvitedUser(token);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPasswordCreated, setIsPasswordCreated] = useState(false);
  const initialData: NewUserData = {
    email: '',
    password: '',
    confirm: '',
    eula: ''
  };

  /**
   * The line break in the error string isn't respected so the
   * error message gets all mashed together. The below code
   * splits the error and wraps in a block element so that it's
   * more readable.
   */
  let errorElement = null;
  if (error) {
    const errors = error.split(',');
    errorElement = (
      <React.Fragment>
        {errors.map((err: string) => (
          <span className="block" key={err}>
            {err}
          </span>
        ))}
      </React.Fragment>
    );
  }

  const form = new FormInstance<NewUserData>(initialData);

  useEffect(() => {
    if (user) {
      form.setFieldValue('email', user.email);
    }
  }, [user]);

  function submit(data: NewUserData) {
    setIsUpdating(true);
    PostJSON<BaseResponse>({
      url: '/user/update_password/',
      params: {
        ...data,
        token
      }
    }).then((response) => {
      setIsUpdating(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Creating Password',
          content: response.message
        });
        return;
      }
      setIsPasswordCreated(true);
      form.getForm().resetForm();
      return;
    });
  }

  const validate = (values: NewUserData): FormErrors => {
    const errors: FormErrors = {};
    if (values.password !== values.confirm) {
      errors.confirm = 'The two passwords must match!';
    }
    return errors;
  };

  const reload = () => {
    window.location.assign('/');
  };

  if (isLoading) {
    return (
      <Card
        style={{
          marginBottom: '100px'
        }}
      >
        <LoadingBlock />
      </Card>
    );
  }

  if (!user) {
    return (
      <Card
        style={{
          marginBottom: '100px'
        }}
      >
        <p className="mb-0">
          This invitation is either invalid or revoked. Contact the person who
          sent you the invitation for more details.
        </p>
      </Card>
    );
  }

  if (!user.passwordToken) {
    return (
      <Card
        style={{
          marginBottom: '100px'
        }}
      >
        <p className="mb-0">
          Either this link has expired, or a password has been created for this
          user. If you created the password for this user, try logging in using
          the credentials.
        </p>
      </Card>
    );
  }

  return (
    <Card
      style={{
        marginBottom: '100px'
      }}
    >
      <Meta title="Get Started" />
      <h1 className="text-sans-lg mb-7">
        <Icon icon={faPlusHexagon} /> Create your password
      </h1>
      <Form form={form} onSubmit={submit} validate={validate}>
        {error && <ErrorCallout className="mb-8">{errorElement}</ErrorCallout>}
        <TextInput label="Email" name="email" disabled />
        <PasswordInput
          label="Password"
          name="password"
          showStrength
          enforceStrength
          required
        />
        <TextInput
          label="Confirm Password"
          name="confirm"
          type="password"
          required
        />
        <Checkbox
          labelElement={
            <React.Fragment>
              I agree with the{' '}
              <a
                href="https://www.caresignal.health/eula"
                target="_blank"
                rel="noreferrer"
              >
                End User Agreement
              </a>
            </React.Fragment>
          }
          name="eula"
          requiredErrorMessage="You must agree to the End User Agreement!"
          required
        />
        {isPasswordCreated === true && (
          <Callout intent="success" className="mb-8">
            Password successfully created, and you've been logged in!
            <Button
              onClick={reload}
              bare
              variant="minimal"
              className="underline"
            >
              Click here to go to the dashboard.
            </Button>
          </Callout>
        )}
        <Button
          type="submit"
          intent="success"
          disabled={isLoading || isUpdating || isPasswordCreated}
          className="mt-8"
          fill
        >
          Create Password
        </Button>
      </Form>
    </Card>
  );
};

export default NewUserPage;
